import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { type WebidStartRequest } from '@/pages/Kyc/webidForm.types';
import StartNewVerification from '@/pages/Kyc/StartNewVerification';
import useApi from '@/hooks/useApi';
import useApp from '@/pages/App.context';
import styles from './WebidForm.module.scss';

/**
 * Dedicated WebID identity-gate form for a guest signer in the QES sign flow.
 * Reached from the provider picker's "WebID" option. Collects the fields WebID
 * needs (DOB, sex, cell, address), starts a WebID transaction, and redirects to
 * WebID. The backend releases the SIGNING lock on success, so on return the
 * signer swipes again to resume with A-Trust.
 */
const WebidForm = () => {
  const { t } = useTranslation(['sign']);
  const { setAppTitle } = useApp();
  const navigate = useNavigate();
  const { workflowId, signerId } = useParams();

  const [error, setError] = useState(false);
  const [webidStart, { loading }] = useApi('sign.webid.start');

  useEffect(() => {
    setAppTitle(t('sign:webid.formTitle'));
  }, [setAppTitle, t]);

  const handleSubmit = async (data: WebidStartRequest) => {
    if (!workflowId) {
      return;
    }
    setError(false);
    const res = await webidStart({ workflowId, ...data });
    if (res?.response?.success) {
      window.location.href = res.response.url;
      return;
    }
    setError(true);
  };

  const handleBack = () => {
    void navigate(`/sign/${workflowId}/${signerId}/document`, {
      replace: true,
    });
  };

  return (
    <div className={styles.pane}>
      {error && (
        <div className={styles.error}>{t('sign:webid.startError')}</div>
      )}
      <StartNewVerification
        loading={loading}
        onBack={handleBack}
        onSubmit={(data) => void handleSubmit(data)}
      />
    </div>
  );
};

export default WebidForm;
